import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDownUp, CheckCircle2, ChevronLeft, ChevronRight, Link2, Mail, Send, Smartphone, UserPlus, Watch, X,
} from 'lucide-react';

import { inviteMobileUser, loadAllMobileAccounts, loadMobileAccounts, resendMobileInvitation } from './api';
import { Empty, Failed, Head, Loading, SearchField } from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import { useXomicsAuth } from './AuthContext';
import { errorMessage } from './ui';
import MobileAccountProfile from './MobileAccountProfile';
import { DeviceArt } from './DeviceArt';
import {
  FilterMenu, Flag, NUMBER, PlaceFilters, PlatformMark, ago, counted, initials, placeMatches, useNow,
} from './MobileParts';

const PAGE = 50;

// Invites someone to the app. XOmics sends the email with a code; the mobile
// backend makes the account and owns the code, which never reaches this page.
export function InviteDialog({ onClose, onInvited }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [state, setState] = useState({ status: 'idle', error: null, code: null, sent: null });
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  async function submit(event) {
    event.preventDefault();
    setState({ status: 'sending', error: null, code: null, sent: null });
    try {
      const sent = await inviteMobileUser({ email: form.email.trim(), firstName: form.firstName.trim(), lastName: form.lastName.trim() });
      setState({ status: 'sent', error: null, code: null, sent });
      onInvited();
    } catch (error) {
      setState({ status: 'idle', error: errorMessage(error), code: error.code || null, sent: null });
    }
  }

  async function resend() {
    setState((current) => ({ ...current, status: 'sending' }));
    try {
      const sent = await resendMobileInvitation(form.email.trim());
      setState({ status: 'sent', error: null, code: null, sent });
      onInvited();
    } catch (error) {
      setState({ status: 'idle', error: errorMessage(error), code: error.code || null, sent: null });
    }
  }

  const ready = form.firstName.trim() && form.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog" role="dialog" aria-modal="true" aria-labelledby="xo-mob-invite-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><UserPlus aria-hidden="true" /></span>
          <div><h2 id="xo-mob-invite-title">{tx('ui.mobile.invite_title')}</h2><p>{tx('ui.mobile.invite_detail')}</p></div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        {state.status === 'sent' ? (
          <div className="xo-mob-dialog-done">
            <CheckCircle2 aria-hidden="true" />
            <strong>{tx('ui.mobile.invite_sent', { email: state.sent.email || form.email.trim() })}</strong>
            <p>{tx('ui.mobile.invite_sent_detail', { date: new Date(state.sent.expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) })}</p>
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={() => { setForm({ firstName: '', lastName: '', email: '' }); setState({ status: 'idle', error: null, code: null, sent: null }); }}>
                {tx('ui.mobile.invite_another')}
              </button>
              <button type="button" className="xo-btn" onClick={onClose}>{tx('ui.mobile.done')}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="xo-mob-form">
            <div className="xo-mob-form-row">
              <label><span>{tx('ui.mobile.first_name')}</span><input value={form.firstName} onChange={set('firstName')} autoComplete="off" maxLength={255} required /></label>
              <label><span>{tx('ui.mobile.last_name')}</span><input value={form.lastName} onChange={set('lastName')} autoComplete="off" maxLength={255} required /></label>
            </div>
            <label><span>{tx('ui.mobile.email')}</span><input type="email" value={form.email} onChange={set('email')} autoComplete="off" maxLength={254} required /></label>
            <div className="xo-mob-invite-preview" aria-hidden="true">
              <Mail /><div><strong>{tx('ui.mobile.invite_preview_title')}</strong><small>{tx('ui.mobile.invite_preview_detail')}</small></div>
            </div>
            {state.error && (
              <p className="xo-alert t-warn" role="alert">
                {state.error}
                {['ALREADY_INVITED', 'INVITATION_EMAIL_FAILED'].includes(state.code) && (
                  <button type="button" className="xo-link" onClick={resend}>{tx('ui.mobile.send_new_code')}</button>
                )}
              </p>
            )}
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.cancel')}</button>
              <button type="submit" className="xo-btn" disabled={!ready || state.status === 'sending'}>
                <Send aria-hidden="true" />{state.status === 'sending' ? tx('ui.mobile.sending') : tx('ui.mobile.send_invitation')}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function Directory({ open }) {
  const { permissions } = useXomicsAuth();
  const mayInvite = permissions.includes('PATIENT_IDENTITY_MANAGE');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ platform: '', device: '', linked: '', sort: 'recent' });
  const [place, setPlace] = useState({ country: '', city: '' });
  const [offset, setOffset] = useState(0);
  const [inviting, setInviting] = useState(false);
  const q = useDebounced(search.trim(), 280);
  const now = useNow(30000);
  useEffect(() => { setOffset(0); }, [q, filters, place]);
  // Only people using the app: accounts still signing up or waiting on an invitation stay out of the list.
  const load = useCallback(() => loadMobileAccounts({ q, status: 'active', ...filters, limit: PAGE, offset }), [q, filters, offset]);
  const list = useLiveResource(['mobile'], load, { reconcileMs: 60000 });
  const setFilter = (field) => (value) => setFilters((current) => ({ ...current, [field]: value }));
  // Country and city filter the whole list, so every account under the other
  // filters is read (it has each account's place), then paged here.
  const [everyone, setEveryone] = useState(null);
  useEffect(() => {
    let live = true;
    setEveryone(null);
    loadAllMobileAccounts({ q, status: 'active', ...filters })
      .then((accounts) => { if (live) setEveryone(accounts); })
      .catch(() => { if (live) setEveryone([]); });
    return () => { live = false; };
  }, [q, filters]);
  const located = Boolean(place.country || place.city);
  const placed = located && everyone !== null
    ? everyone.filter((account) => placeMatches(account, place.country, place.city)) : null;

  const data = !located ? list.data : placed === null ? null : { accounts: placed.slice(offset, offset + PAGE), total: placed.length };
  return (
    <div className="xo-platform-page xo-mobile-page">
      <Head title={tx('ui.mobile.accounts_title')} subtitle={tx('ui.mobile.accounts_subtitle')} />

      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          <SearchField className="xo-product-search" value={search} onChange={setSearch} placeholder={tx('ui.mobile.search_accounts')} autoComplete="off" />
          {mayInvite && (
            <button type="button" className="xo-btn xo-product-new-button" onClick={() => setInviting(true)}>
              <UserPlus aria-hidden="true" />{tx('ui.mobile.invite_someone')}
            </button>
          )}
        </div>
        <div className="xo-product-section-row">
          <span className="xo-mob-list-count">{data ? counted('ui.mobile.active_accounts_count', data.total) : ''}</span>
          <div className="xo-product-toolbar-actions xo-mob-filter-row">
            <PlaceFilters places={everyone || []} country={place.country} city={place.city} onChange={setPlace} />
            <FilterMenu icon={Watch} label={tx('ui.mobile.wearable')} value={filters.device} onChange={setFilter('device')} options={[
              { value: '', label: tx('ui.mobile.any_wearable') },
              { value: 'any', label: tx('ui.mobile.has_wearable') },
              { value: 'r01', label: tx('ui.mobile.origin_r01') },
              { value: 'b01', label: tx('ui.mobile.origin_b01') },
              { value: 'none', label: tx('ui.mobile.no_wearable') },
            ]} />
            <FilterMenu icon={Link2} label={tx('ui.mobile.patient_link')} value={filters.linked} onChange={setFilter('linked')} options={[
              { value: '', label: tx('ui.mobile.any_patient_link') },
              { value: 'yes', label: tx('ui.mobile.linked_to_patient') },
              { value: 'no', label: tx('ui.mobile.not_linked') },
            ]} />
            <FilterMenu icon={Smartphone} label={tx('ui.mobile.phone_platform')} value={filters.platform} onChange={setFilter('platform')} options={[
              { value: '', label: tx('ui.mobile.any_phone') },
              { value: 'ios', label: 'iOS' },
              { value: 'android', label: 'Android' },
            ]} />
            <FilterMenu icon={ArrowDownUp} label={tx('ui.mobile.sort')} value={filters.sort} onChange={setFilter('sort')} options={[
              { value: 'recent', label: tx('ui.mobile.sort_recent') },
              { value: 'joined', label: tx('ui.mobile.sort_joined') },
              { value: 'name', label: tx('ui.mobile.sort_name') },
            ]} />
          </div>
        </div>
      </div>

      <div className="xo-data-surface xo-mob-directory">
        {(located || list.loading) && data === null && <Loading label={tx('ui.mobile.reading_accounts')} />}
        {!located && list.error && data === null && <Failed message={list.error} onRetry={list.refresh} />}
        {data && data.accounts.length === 0 && <Empty title={tx('ui.mobile.no_accounts_match')} />}
        {data && data.accounts.length > 0 && (
          <>
            <div className="xo-mob-directory-labels" aria-hidden="true">
              <span>{tx('ui.mobile.person')}</span><span>{tx('ui.mobile.device_and_place')}</span>
              <span>{tx('ui.mobile.patient')}</span><span>{tx('ui.mobile.last_seen')}</span><span />
            </div>
            <AnimatePresence initial={false}>
              {data.accounts.map((account, index) => (
                <motion.button type="button" key={account.uuid} className="xo-mob-account-row" onClick={() => open(account.uuid)}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15, delay: Math.min(index, 20) * 0.01 }}>
                  <span className="xo-mob-person">
                    <span className={`xo-mob-avatar is-${account.platform || 'none'}`}>{initials(account.name, account.email)}</span>
                    <span><strong>{account.name || account.email || tx('ui.mobile.no_name_yet')}</strong><small>{account.email}</small></span>
                  </span>
                  <span className="xo-mob-device-cell">
                    {account.platform && <PlatformMark platform={account.platform} />}
                    {account.devices.r01 > 0 && <span title={tx('ui.mobile.origin_r01')}><DeviceArt origin="r01" size={24} /></span>}
                    {account.devices.b01 > 0 && <span title={tx('ui.mobile.origin_b01')}><DeviceArt origin="b01" size={24} /></span>}
                    {account.country && <Flag code={account.country} />}
                    <small>{account.city || ''}</small>
                  </span>
                  <span className="xo-mob-patient-cell">
                    {account.patient === null ? <small className="is-muted">{tx('ui.mobile.not_linked')}</small> : (
                      <span><Link2 aria-hidden="true" /><strong>{account.patient.name || `#${account.patient.id}`}</strong>
                        <small>{[account.patient.code, account.patient.project].filter(Boolean).join(' · ')}</small></span>
                    )}
                  </span>
                  <span className="xo-mob-seen-cell">
                    <strong>{ago(account.lastSeenAt === null ? account.lastLoginAt : account.lastSeenAt, now)}</strong>
                    <small>{tx('ui.mobile.joined_on', { date: new Date(account.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) })}</small>
                  </span>
                  <ChevronRight aria-hidden="true" />
                </motion.button>
              ))}
            </AnimatePresence>
            <div className="xo-mob-pager">
              <span>{tx('ui.mobile.showing_range', { from: NUMBER.format(offset + 1), to: NUMBER.format(offset + data.accounts.length), total: NUMBER.format(data.total) })}</span>
              <button type="button" className="xo-mob-icon-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} aria-label={tx('ui.mobile.previous_page')}><ChevronLeft /></button>
              <button type="button" className="xo-mob-icon-button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)} aria-label={tx('ui.mobile.next_page')}><ChevronRight /></button>
            </div>
          </>
        )}
      </div>
      <AnimatePresence>{inviting && <InviteDialog onClose={() => setInviting(false)} onInvited={list.refresh} />}</AnimatePresence>
    </div>
  );
}

export default function MobileAccounts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const account = searchParams.get('account');
  const open = useCallback((uuid) => {
    const next = new URLSearchParams(searchParams);
    next.set('account', uuid);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams, setSearchParams]);
  const back = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('account');
    next.delete('section');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  if (account) return <MobileAccountProfile uuid={account} onBack={back} />;
  return <Directory open={open} />;
}
